import { Apartment } from '@/data/apartments';
import { useLanguage } from '@/i18n/LanguageContext';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { MapPin, Users, BedDouble, Bath, Star, ArrowRight } from 'lucide-react';

interface Props {
  apartment: Apartment;
  index: number;
}

const ApartmentCard = ({ apartment, index }: Props) => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const details = t.apartmentDetails[apartment.id];

  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-50px' }}
      transition={{ duration: 0.7, delay: index * 0.15 }}
      className="glass-card gold-border group overflow-hidden rounded-lg">

      {/* Image */}
      <div
        className="relative h-64 cursor-pointer overflow-hidden"
        onClick={() => navigate(`/apartment/${apartment.id}`)}>

        <img
          src={apartment.image}
          alt={apartment.name}
          className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110"
          loading="lazy"
          width="672"
          height="448" />

        <div className="absolute inset-0 bg-gradient-to-t from-background/80 to-transparent" />
        {(apartment.rating || apartment.bookingRating) && (
          <div className="absolute bottom-4 left-4 flex items-center gap-3">
            {apartment.rating && (
              <div className="flex items-center gap-1.5 rounded-full bg-background/80 px-3 py-1 backdrop-blur-sm">
                <Star className="h-4 w-4 fill-primary text-primary" />
                <span className="text-sm font-medium text-primary">{apartment.rating}</span>
                <span className="text-xs text-muted-foreground">
                  ({apartment.reviewCount} {t.apartments.reviews})
                </span>
              </div>
            )}
            {apartment.bookingRating && (
              <div className="flex items-center gap-1.5 rounded-full bg-background/80 px-3 py-1 backdrop-blur-sm">
                <span className="text-xs font-bold text-[#003580]">B</span>
                <span className="text-sm font-medium text-primary">{apartment.bookingRating}</span>
                <span className="text-xs text-muted-foreground">
                  ({apartment.bookingReviewCount} {t.apartments.reviews})
                </span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-6">
        <h3 className="font-serif text-2xl tracking-wide text-foreground">{apartment.name}</h3>
        <div className="mt-2 flex items-center gap-2 text-muted-foreground">
          <MapPin className="h-3.5 w-3.5 text-primary/70" />
          <span className="text-sm">{details?.location}</span>
        </div>

        <p className="mt-4 font-sans text-sm leading-relaxed text-muted-foreground">
          {details?.description}
        </p>

        {/* Features */}
        <div className="mt-5 flex flex-wrap gap-4 border-t border-border/30 pt-5">
          <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
            <Users className="h-4 w-4 text-primary/60" />
            <span>{apartment.guests} {t.apartments.guests}</span>
          </div>
          <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
            <BedDouble className="h-4 w-4 text-primary/60" />
            <span>{apartment.bedrooms} {t.apartments.bedrooms}</span>
          </div>
          <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
            <Bath className="h-4 w-4 text-primary/60" />
            <span>{apartment.bathrooms} {t.apartments.bathrooms}</span>
          </div>
        </div>

        {/* Pricing note */}
        <p className="mt-4 font-serif-elegant italic text-primary/70 text-base font-semibold">
          {t.apartments.seasonalPricing}. {t.apartments.longStay}.
        </p>

        {/* CTA */}
        <button
          onClick={() => navigate(`/apartment/${apartment.id}`)}
          className="mt-5 flex w-full items-center justify-center gap-2 rounded-sm border border-primary/30 py-3 font-sans text-xs uppercase tracking-[0.2em] text-primary transition-all duration-300 hover:bg-primary hover:text-primary-foreground">
          {t.apartments.moreInfo}
          <ArrowRight className="h-3.5 w-3.5" />
        </button>
      </div>
    </motion.div>);
};

export default ApartmentCard;
